import { FC } from "react";
import { UserSearchItem } from "src/components";
import useSingleStoreUser from "src/hooks/useSingleStoreUser/useSingleStoreUser";
import useStore from "src/hooks/useStoreId/useStoreId";
import useRegion from "../../hooks/useRegion/useRegion";
import AdminObservationOutcomeBadge from "src/features/observationsAdmin/components/AdminObservationOutcomeBadge/AdminObservationOutcomeBadge";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import { useAreas } from "../../hooks";
import dayjs from "dayjs";
import { Button } from "src/components/ui/button";

export interface InstanceReportSummaryProps {
	completed?: boolean;
	compliant?: boolean;
	createdBy?: IFormCreator | null;
	passedThreshold?: boolean;
	completedAt?: string;
	observationType?: IObservationType;
	storeId?: number | null;
	userId?: number | null;
	overallScore?: number | null;
	overallScoreBreakdown?: string | undefined;
	greatConversationGradePerformance?: IGreatConversationGradePerformance;
	graded?: boolean;
	showGradingReport?: boolean;
	gradingHeadingRef?: React.RefObject<HTMLDivElement>;
	observedAt?: string;
	month?: number;
}

const InstanceReportSummary: FC<InstanceReportSummaryProps> = ({
	compliant = false,
	completedAt,
	completed,
	createdBy,
	passedThreshold,
	observationType,
	userId,
	storeId,
	overallScore,
	greatConversationGradePerformance,
	showGradingReport,
	graded,
	gradingHeadingRef,
	observedAt,
	overallScoreBreakdown,
	month
}) => {
	const { data: user } = useSingleStoreUser(userId, {
		enabled: Boolean(userId)
	});

	const { data: store } = useStore(storeId, { enabled: Boolean(storeId) });

	const { data: region } = useRegion(
		{ regionId: store?.region },
		{ enabled: Boolean(store?.region) }
	);

	const { data: areas } = useAreas();

	const showEmployee = checkObservationFormType(observationType) === "user";
	const showStore = observationType !== "INDIVIDUAL" && store;

	const scrollToBottom = () => {
		if (gradingHeadingRef && gradingHeadingRef.current) {
			gradingHeadingRef?.current?.scrollIntoView({ behavior: "smooth" });
		}
	};

	return (
		<div data-testid="component.InstanceReportSummary" className="relative">
			<div className="flex items-center mb-4">
				{observationType && (
					<div className="basis-1/3">
						<AdminObservationOutcomeBadge
							observationType={observationType}
							isCompliant={compliant}
							isCompleted={completed}
							isPassedThreshold={passedThreshold}
							overallScore={overallScore}
							overallScoreBreakdown={overallScoreBreakdown}
						/>
					</div>
				)}

				{month !== undefined && (
					<h1 className="text-xl font-semibold text-center basis-1/3">
						{dayjs().month(month).format("MMMM")}
					</h1>
				)}

				{showGradingReport && !graded ? (
					<div className="mb-8 basis-1/3 text-right">
						<Button
							onClick={scrollToBottom}
							variant="outline"
							className="min-w-[220px]"
						>
							Grade Employee
						</Button>
					</div>
				) : showGradingReport && graded ? (
					<div className="mb-4 ml-auto flex justify-end flex-grow">
						<span className="inline-flex items-center gap-2 bg-foreground text-white py-2 px-4 rounded-md text-base">
							<span>Grading: </span>
							<span className="font-normal">
								{greatConversationGradePerformance}
							</span>
						</span>
					</div>
				) : (
					<div className="basis-1/3" />
				)}
			</div>

			<div className="bg-accent p-4 rounded-lg">
				<div className="flex flex-col md:flex-row flex-wrap gap-4 md:gap-14">
					{showEmployee && (
						<div className="flex flex-col items-start">
							<p className="font-bold mb-2">Employee Name</p>

							{user && <UserSearchItem user={user} />}
						</div>
					)}

					{showStore && (
						<div className="flex flex-col items-start">
							<p className="font-bold mb-2">Store</p>

							{store && <p className="text-sm font-bold mb-1">{store.name}</p>}

							{region && region.name && (
								<p className="text-sm">
									{region.name},{" "}
									{areas &&
										areas.results
											.filter((area) => area.id === store.area)
											.map((area) => area.name)}
								</p>
							)}
						</div>
					)}

					<div>
						<p className="font-bold mb-2">Date of Observation</p>

						{observedAt && (
							<p data-testid="obs-observed-at" className="text-sm mb-1">
								Observed on: {observedAt}
							</p>
						)}

						{completedAt && (
							<p data-testid="obs-recorded-at" className="text-sm">
								Added on: {completedAt}
							</p>
						)}
					</div>

					<div>
						<p className="font-bold mb-2">Observed by</p>

						<div>
							{createdBy ? (
								<div>
									<p className="text-sm font-medium mb-1">{createdBy.name}</p>

									<p className="text-sm">{createdBy.role}</p>
								</div>
							) : (
								<p className="text-sm">Unknown</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default InstanceReportSummary;
